import React, { useMemo } from 'react';
import { Alert, Button, Flex, Segmented, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDeliveryDashboard } from '../../hooks/useDeliveryDashboard';
import { DELIVERY_EVENTS_PER_PAGE } from '../../stores/deliveryDashboard.store';
import DeliveryKpis from '../../components/DeliveryDashboard/DeliveryKpis.component';
import DeliveryAlertsPanel from '../../components/DeliveryDashboard/DeliveryAlertsPanel.component';
import DeliveryMatrix from '../../components/DeliveryDashboard/DeliveryMatrix.component';
import DeliveryFeed from '../../components/DeliveryDashboard/DeliveryFeed.component';
import DeliveryDetailDrawer from '../../components/DeliveryDashboard/DeliveryDetailDrawer.component';

import '../../components/DeliveryDashboard/DeliveryDashboard.styles.css';
import './AdminDeliveriesPage.styles.css';

const RANGE_OPTIONS = [3, 7, 14];

const AdminDeliveriesPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    rangeDays, window, groupBy, summary, alerts, matrix, filters, events, eventsTotal, eventsPage, detail,
    isLoading, isEventsLoading, updatedAt, error,
    refresh, setRangeDays, setGroupBy, setFilters, clearFilters, setEventsPage, openEvent, closeEvent,
  } = useDeliveryDashboard();

  const timeFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { timeStyle: 'short' }), [i18n.language]);

  const filterLabels = useMemo(() => {
    const byKey = new Map(matrix.map((row) => [row.key, row.label]));
    const fromAlerts = new Map<string, string>();
    alerts?.blockedTargets.forEach((target) => {
      fromAlerts.set(target.channelUuid, target.channelLink);
      fromAlerts.set(target.telegramAccountUuid, target.telegramAccountPhone);
    });
    const label = (key?: string) => (key ? byKey.get(key) ?? fromAlerts.get(key) : undefined);
    return { account: label(filters.telegramAccountUuid), channel: label(filters.channelUuid) };
  }, [matrix, alerts, filters.telegramAccountUuid, filters.channelUuid]);

  const matrixFilter = (key: string) => (groupBy === 'account' ? { telegramAccountUuid: key } : { channelUuid: key });

  return (
    <div className="admin-deliveries-page">
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={16}>
        <div>
          <h1 className="admin-deliveries-title">{t('delivery-dashboard', { ns: 'titles' })}</h1>
          <p className="admin-deliveries-subtitle">{t('delivery-dashboard-subtitle', { ns: 'titles', count: rangeDays })}</p>
        </div>
        <Flex gap={8} align="center" wrap="wrap">
          <Segmented<number>
            value={rangeDays}
            onChange={setRangeDays}
            options={RANGE_OPTIONS.map((days) => ({ value: days, label: t('delivery-range', { ns: 'labels', count: days }) }))}
          />
          <Button icon={<ReloadOutlined />} loading={isLoading} onClick={refresh}>{t('refresh', { ns: 'buttons' })}</Button>
          {updatedAt && (
            <Typography.Text type="secondary">{t('delivery-updated-at', { ns: 'labels', time: timeFormat.format(updatedAt) })}</Typography.Text>
          )}
        </Flex>
      </Flex>

      {error && <Alert type="error" showIcon message={t(error, { ns: 'errors', defaultValue: t('unknown-error', { ns: 'errors' }) })} />}

      <DeliveryKpis summary={summary} rangeDays={rangeDays} onOutcomeClick={(outcomes) => setFilters({ outcomes })} />

      <DeliveryAlertsPanel
        alerts={alerts}
        onShowDelayed={() => setFilters({ outcomes: ['delayed'] })}
        onShowTarget={(channelUuid, telegramAccountUuid) => setFilters({ channelUuid, telegramAccountUuid, outcomes: [] })}
        onShowAccount={(telegramAccountUuid) => setFilters({ telegramAccountUuid, outcomes: [] })}
      />

      <DeliveryMatrix
        rows={matrix}
        window={window}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        onRowClick={(key) => setFilters({ ...matrixFilter(key), day: undefined })}
        onCellClick={(key, day) => setFilters({ ...matrixFilter(key), day })}
      />

      <DeliveryFeed
        events={events}
        total={eventsTotal}
        page={eventsPage}
        perPage={DELIVERY_EVENTS_PER_PAGE}
        isLoading={isEventsLoading}
        filters={filters}
        errorCodes={(summary?.topErrors ?? []).map((e) => e.code)}
        filterLabels={filterLabels}
        onFiltersChange={setFilters}
        onClearFilters={clearFilters}
        onPageChange={setEventsPage}
        onOpen={openEvent}
      />

      <DeliveryDetailDrawer detail={detail} onClose={closeEvent} />
    </div>
  );
};

export default AdminDeliveriesPage;
